import { useLocalObservable } from 'mobx-react-lite';
import { useEffect } from 'react';

import { Call } from '@gem/call/CallStore/Call';
import { useCallContext } from '@gem/call/CallView/CallView.context';
import {
  PermissionsRequestStatuses,
  PermissionsTypes,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { IcClose24Icon, IcDoneIcon } from '@gem/icons';
import { useTranslation } from '@gem/modules/i18n';
import { IPopover, IPopoverMenuItem } from '@gem/ui-kit';

interface IRequestedPermissionsPanelPresenter {
  onClose(): void;
  ongoingCall: Call;
  channelId: string | null;
  permissionsRequests: Map<PermissionsTypes, string[]>;
  isPermissionsRequestsEmpty: boolean;
  setPopover(popover: IPopover<HTMLElement>): void;
  popover: IPopover<HTMLElement>;
  actions: IPopoverMenuItem[];
  setPermissionsRequestForAll(status: PermissionsRequestStatuses): Promise<void>;
  onBack(): void;
}

interface IRequestedPermissionsPanelPresenterProps {
  popover: IPopover<HTMLElement>;
}

export const useRequestedPermissionsPanelPresenter = (
  props: IRequestedPermissionsPanelPresenterProps
): IRequestedPermissionsPanelPresenter => {
  const { call, callController } = useCallContext();
  const { t } = useTranslation();

  const presenter = useLocalObservable(() => ({
    popover: props.popover,

    onBack(): void {
      call.viewState.hidePermissionsRequests();
    },

    onClose(): void {
      call.viewState.hideMembers();
      call.viewState.hidePermissionsRequests();
    },

    get channelId(): string | null {
      return presenter.ongoingCall.channelId || null;
    },

    get ongoingCall(): Call {
      return call;
    },

    get permissionsRequests(): Map<PermissionsTypes, string[]> {
      return presenter.ongoingCall.permissionsRequests;
    },

    get isPermissionsRequestsEmpty(): boolean {
      return presenter.ongoingCall.isPermissionsRequestsEmpty;
    },

    setPopover(popover: IPopover<HTMLElement>): void {
      presenter.popover = popover;
    },

    async setPermissionsRequestForAll(status: PermissionsRequestStatuses): Promise<void> {
      await callController.updatePermissionRequestsForAll({
        channelId: call.channelId!,
        status: status,
      });
      presenter.popover.close();
    },

    get actions(): IPopoverMenuItem[] {
      return [
        {
          key: 'approve',
          title: t('calls_participants_admit_all_button'),
          onClick: () => presenter.setPermissionsRequestForAll(PermissionsRequestStatuses.Approved),
          icon: IcDoneIcon,
        },
        {
          key: 'reject',
          title: t('call_permissions_decline_all'),
          onClick: () => presenter.setPermissionsRequestForAll(PermissionsRequestStatuses.Rejected),
          icon: IcClose24Icon,
        },
      ];
    },
  }));

  useEffect(() => {
    if (!presenter.ongoingCall.isStarted) {
      presenter.onClose();
    }
  }, [presenter.ongoingCall.isStarted]);

  useEffect(() => {
    if (presenter.isPermissionsRequestsEmpty) {
      presenter.onClose();
    }
  }, [presenter.isPermissionsRequestsEmpty]);

  useEffect(() => {
    presenter.setPopover(props.popover);
  }, [props.popover]);

  return presenter;
};
